import { forwardRef, TextareaHTMLAttributes, useCallback, useEffect, useId, useRef } from 'react';
import { cn } from '../../../utils/cn';
import { controlClass, describedBy, Field, FieldOwnProps } from '../Field/Field';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, Omit<FieldOwnProps, 'labelAction'> {
  autoResize?: boolean;
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, rows = 3, autoResize = true, maxLength, showCount, className, containerClassName, id, value, onChange, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id || autoId;
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoResize) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 360)}px`;
  }, [autoResize]);

  useEffect(resize, [value, resize]);

  const count = typeof value === 'string' ? value.length : 0;
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={inputId}
      className={containerClassName}
      labelAction={showCount && maxLength ? <span className="text-xs text-ink-400 tabular">{count}/{maxLength}</span> : null}
    >
      <textarea
        ref={setRefs}
        id={inputId}
        rows={rows}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(inputId, error, hint)}
        className={cn(controlClass({ error, disabled: rest.disabled }), 'resize-none px-3.5 py-2.5 leading-relaxed', className)}
        {...rest}
      />
    </Field>
  );
});

export default Textarea;
